import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram, ChevronDown } from "lucide-react";
import { WORK_CATEGORIES, COLLECTIONS } from "../data/site";
import { CurrencyToggle } from "../contexts/CurrencyContext";

const LEARN = [
  { to: "/youth-art-classes", id: "youth-art-classes", label: "Youth Art Classes", note: "Ages 8–12 · Saturdays · Denver" },
  { to: "/paint-n-sip#book", id: "paint-n-sip", label: "Reserve a Paint-n-Sip", note: "Bob'Bae Rosé · parties, teams & kids" },
  { to: "/live-painting", id: "live-painting", label: "Book Live Painting", note: "Weddings, openings & corporate events" },
  { to: "/books", id: "books", label: "Books", note: "You Are An Artist! & more" },
  { to: "/services", id: "services", label: "All Services", note: "Murals, iPad portraits, face painting" },
];

const NAV = [
  { to: "/work", label: "Work", dropdown: "work" },
  { to: "/prints", label: "Prints" },
  { to: "/originals", label: "Originals" },
  { to: "/murals", label: "Murals" },
  { to: "/youth-art-classes", label: "Learn", dropdown: "learn" },
  { to: "/about", label: "About" },
  { to: "/shop", label: "Shop" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [workOpen, setWorkOpen] = useState(false);
  const [learnOpen, setLearnOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setWorkOpen(false);
    setLearnOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <Header
        open={open}
        setOpen={setOpen}
        workOpen={workOpen}
        setWorkOpen={setWorkOpen}
        learnOpen={learnOpen}
        setLearnOpen={setLearnOpen}
      />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function Header({ open, setOpen, workOpen, setWorkOpen, learnOpen, setLearnOpen }) {
  const location = useLocation();
  return (
    <header
      className="sticky top-0 z-50 bg-background/85 backdrop-blur-md border-b border-border"
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 md:h-24 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2"
          data-testid="nav-logo"
        >
          <img
            src="/jgj-logo-dark.png"
            alt="Joseph Graves Jr."
            className="h-14 md:h-20 w-auto object-contain"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10" aria-label="Primary">
          {NAV.map((item) =>
            item.dropdown === "work" ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setWorkOpen(true)}
                onMouseLeave={() => setWorkOpen(false)}
              >
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `label flex items-center gap-1 link-underline ${
                      isActive ? "text-foreground" : "text-foreground/80"
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase()}-link`}
                >
                  {item.label}
                  <ChevronDown className="h-3 w-3" />
                </NavLink>
                {workOpen && (
                  <div
                    className="absolute left-0 top-full pt-4"
                    data-testid="nav-work-dropdown"
                  >
                    <div className="min-w-[280px] bg-background border border-border shadow-lg">
                      <Link
                        to="/work"
                        className="block px-5 py-3 label text-[hsl(var(--ink-soft))] hover:bg-[hsl(var(--surface))]"
                        data-testid="nav-work-all"
                      >
                        All Work →
                      </Link>
                      <div className="h-px bg-border" />
                      {WORK_CATEGORIES.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/work/${c.slug}`}
                          className="block px-5 py-3 serif text-lg hover:bg-[hsl(var(--surface))]"
                          data-testid={`nav-work-${c.slug}`}
                        >
                          {c.label}
                        </Link>
                      ))}
                      <div className="h-px bg-border" />
                      <p className="px-5 pt-3 pb-1 label text-[hsl(var(--ink-soft))]">
                        Collections
                      </p>
                      {COLLECTIONS.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/collections/${c.slug}`}
                          className="block px-5 py-2.5 serif italic text-base hover:bg-[hsl(var(--surface))]"
                          data-testid={`nav-collection-${c.slug}`}
                        >
                          {c.label}
                        </Link>
                      ))}
                      <Link
                        to="/collections"
                        className="block px-5 py-3 label text-[hsl(var(--ink-soft))] hover:bg-[hsl(var(--surface))]"
                        data-testid="nav-collections-all"
                      >
                        All Collections →
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ) : item.dropdown === "learn" ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setLearnOpen(true)}
                onMouseLeave={() => setLearnOpen(false)}
              >
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `label flex items-center gap-1 link-underline ${
                      isActive || LEARN.some((l) => location.pathname.startsWith(`/${l.id}`)) ? "text-foreground" : "text-foreground/80"
                    }`
                  }
                  data-testid="nav-learn-link"
                >
                  {item.label}
                  <ChevronDown className="h-3 w-3" />
                </NavLink>
                {learnOpen && (
                  <div className="absolute left-0 top-full pt-4" data-testid="nav-learn-dropdown">
                    <div className="min-w-[340px] bg-background border border-border shadow-lg">
                      {LEARN.map((l) => (
                        <Link
                          key={l.to}
                          to={l.to}
                          className="block px-5 py-3 hover:bg-[hsl(var(--surface))]"
                          data-testid={`nav-learn-${l.id}`}
                        >
                          <span className="serif text-lg block">{l.label}</span>
                          <span className="label text-[hsl(var(--ink-soft))] block mt-0.5">{l.note}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : item.external ? (
              <a
                key={item.to}
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                className="label link-underline text-foreground/80"
                data-testid={`nav-${item.label.toLowerCase()}-link`}
              >
                {item.label}
              </a>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `label link-underline ${
                    isActive ? "text-foreground" : "text-foreground/80"
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase()}-link`}
              >
                {item.label}
              </NavLink>
            )
          )}
          <CurrencyToggle className="ml-4 pl-4 border-l border-border" />
        </nav>

        <button
          aria-label="Toggle menu"
          className="md:hidden p-2 -mr-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="md:hidden border-t border-border bg-background max-h-[calc(100vh-80px)] overflow-y-auto"
          data-testid="nav-mobile-menu"
        >
          <div className="px-6 py-6 space-y-1">
            {NAV.map((item) => (
              <div key={item.label}>
                {item.external ? (
                  <a
                    href={item.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block py-3 serif text-2xl"
                    data-testid={`nav-mobile-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    to={item.to}
                    className="block py-3 serif text-2xl"
                    data-testid={`nav-mobile-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </Link>
                )}
                {item.dropdown === "learn" && (
                  <div className="pl-4 pb-2 space-y-1 border-l border-border ml-1">
                    {LEARN.map((l) => (
                      <Link
                        key={l.to}
                        to={l.to}
                        className="block py-1.5 text-sm text-foreground/75"
                        data-testid={`nav-mobile-learn-${l.id}`}
                      >
                        {l.label}
                      </Link>
                    ))}
                  </div>
                )}
                {item.dropdown === "work" && (
                  <div className="pl-4 pb-2 space-y-1 border-l border-border ml-1">
                    {WORK_CATEGORIES.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/work/${c.slug}`}
                        className="block py-1.5 text-sm text-foreground/75"
                        data-testid={`nav-mobile-work-${c.slug}`}
                      >
                        {c.label}
                      </Link>
                    ))}
                    <p className="pt-3 pb-1 label text-[hsl(var(--ink-soft))]">
                      Collections
                    </p>
                    {COLLECTIONS.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/collections/${c.slug}`}
                        className="block py-1.5 text-sm serif italic text-foreground/75"
                        data-testid={`nav-mobile-collection-${c.slug}`}
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-3 mt-3 border-t border-border flex flex-wrap gap-x-5 gap-y-2">
              {[
                { to: "/commissions", label: "Commissions" },
                { to: "/contact", label: "Contact" },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="label text-foreground/70"
                  data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer
      className="border-t border-border mt-32 bg-[hsl(var(--surface))]"
      data-testid="site-footer"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <p className="label mb-4">Studio</p>
            <h3 className="serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Art rooted in Black experience.
              <br />
              <span className="italic text-[hsl(var(--ink-soft))]">
                Made in Denver.
              </span>
            </h3>
            <p className="mt-8 max-w-md text-sm text-[hsl(var(--ink-soft))] leading-relaxed">
              Community work:{" "}
              <a
                href="https://www.gravescollective.com"
                target="_blank"
                rel="noreferrer"
                className="link-underline text-foreground"
                data-testid="footer-graves-collective"
              >
                Graves Collective →
              </a>
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="label mb-4">Navigate</p>
            <ul className="space-y-2 text-sm">
              {[
                { to: "/work", label: "Work" },
                { to: "/originals", label: "Available Originals" },
                { to: "/prints", label: "Prints" },
                { to: "/shop", label: "Shop" },
                { to: "/murals", label: "Murals" },
                { to: "/books", label: "Books" },
                { to: "/youth-art-classes", label: "Youth Art Classes" },
                { to: "/commissions", label: "Commissions" },
                { to: "/collectors", label: "Collectors" },
                { to: "/about", label: "About" },
                { to: "/cv", label: "CV" },
                { to: "/press", label: "Press & PR" },
                { to: "/journal", label: "Studio Journal" },
                { to: "/faq", label: "FAQ" },
                { to: "/trade", label: "Trade Program" },
                { to: "/contact", label: "Contact" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="link-underline"
                    data-testid={`footer-${l.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="label mb-4">Follow</p>
            <a
              href="https://www.instagram.com/artbyjoseph_graves_jr"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 link-underline"
              data-testid="footer-instagram"
            >
              <Instagram className="h-4 w-4" />
              <span className="text-sm">@artbyjoseph_graves_jr</span>
            </a>
            <div className="mt-6 space-y-2">
              <Link
                to="/prints"
                className="block text-xs text-[hsl(var(--ink-soft))] link-underline"
                data-testid="footer-prints"
              >
                Collector edition prints →
              </Link>
              <Link
                to="/prints#open-editions"
                className="block text-xs text-[hsl(var(--ink-soft))] link-underline"
                data-testid="footer-open-editions"
              >
                Open editions · Gelato (coming soon) →
              </Link>
              <Link
                to="/paint-n-sip"
                className="block text-xs text-[hsl(var(--ink-soft))] link-underline"
                data-testid="footer-paint-n-sip"
              >
                Bob'Bae Paint n Sip →
              </Link>
              <Link
                to="/live-painting"
                className="block text-xs text-[hsl(var(--ink-soft))] link-underline"
                data-testid="footer-live-painting"
              >
                Book live event painting →
              </Link>
              <Link
                to="/studio-visit"
                className="block text-xs text-[hsl(var(--ink-soft))] link-underline"
                data-testid="footer-studio-visit"
              >
                Visit the studio →
              </Link>
              <Link
                to="/services"
                className="block text-xs text-[hsl(var(--ink-soft))] link-underline"
                data-testid="footer-services"
              >
                Studio services →
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-[hsl(var(--ink-soft))]">
            © {new Date().getFullYear()} Joseph Graves Jr. All works and writing
            reserved.
          </p>
          <p className="text-xs text-[hsl(var(--ink-soft))]">Denver, Colorado</p>
        </div>
      </div>
    </footer>
  );
}
